/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */

import ApplicationFeatureFlags from '@ion/core/src/security/ApplicationFeatureFlags';
import {
  TRACKING_PAGE2
} from './Features';

/**
 * Provides the Authoritative list of Feature Flags which are supported by iOn Suite.
 * The `FeatureFlag` enumerations defined herein represent iOn Suite aware Role based
 * Features, and / or UI Only Features.
 *
 * @see @ion/applications/src/telemetry/APMInitializer#APMFeatureFlag
 */
export enum FeatureFlag {
  APM = 'apm',
  APM_DEBUG = 'apmdebug',
  // shows the New Theme Toggle (`?features=dls`) - a UI only flag, no role
  // grants it - see `@/security/themeAccess` ...
  DLS = 'dls',
  FM_TRACKING = 'tracking2'
}

/**
 * Provides mapping of pre-defined feature flag constants as keys where each key
 * represents a supported feature flag (which, for example, can be used in the
 * query string, or via a build argument, etc.).
 *
 * While a Feature Flag's keys and values can be arbitrarily defined, generally,
 * their values are either:
 *
 *   - A Role Feature Id (guid): (either seeded and / or unseeded)
 *   - A boolean value of true (for non-Role based Feature Flags). Note the value
 *     of true simply indicates that the given feature flag is supported.
 *
 * The following examples demonstrates two pre-defined feature flags, the first
 * `SOME_PAGE` mapping to a Role Feature's GUID, and the second `SOME_COMPONENT`
 * having a value of `true`, which simply indicates that the feature flag is
 * supported (but is unrelated to Role Features):
 *
 * ```typescript
 *     import FeatureFlags from '@/security/FeatureFlags';
 *
 *     FeatureFlags.applyFeatureFlags(queryFeatures);
 *     const featureRoles = FeatureFlags.getRoleFeatures();
 *     Features.updateRoleFeatures(FeatureFlags.getRoleFeatures());
 *   ```
 */
class FeatureFlags extends ApplicationFeatureFlags {
  /**
   * Features are defined in key/value pairs.
   * Key defines the query param value and value corresponds to the feature in the application.
   *  ```typescript
   *  import {FEATURE_PAGE} from './Features';
   *  _feature = {
   *    FEATURE1 : FEATURE_PAGE
   *  }
   * ```
   */
  protected _features = {
    [FeatureFlag.APM]         : true,
    [FeatureFlag.APM_DEBUG]   : true,
    [FeatureFlag.DLS]         : true,
    [FeatureFlag.FM_TRACKING] : TRACKING_PAGE2
  };

  /**
   * Provides an iOn Suite specific overload of the base `isEnabled` method which allows
   * for determining if a given iOn Suite specific `FeatureFlag` is `enabled`.
   *
   * ```typescript
   *   FeatureFlags.isEnabled(FeatureFlag.FM_TRACKING); // true if enabled, otherwise false
   * ```
   *
   * @param  feature A `FeatureFlag` for which to determine if it is `enabled`.
   * @return `true` if the given `FeatureFlag` is enabled, otherwise, `false`.
   */
  public isEnabled(feature: FeatureFlag): boolean {
    return super.isEnabled(feature);
  }

  /**
   * Provides an iOn Suite specific overload of the base `isDisabled` method which allows
   * for determining if a given iOn Suite specific `FeatureFlag` is `disabled`.
   *
   * ```typescript
   *   FeatureFlags.isDisabled(FeatureFlag.FM_TRACKING); // true if disabled, otherwise false
   * ```
   *
   * @param  feature A `FeatureFlag` for which to determine if it is `disabled`.
   * @return `true` if the given `FeatureFlag` is `disabled`, otherwise, `false`.
   */
  public isDisabled(feature: FeatureFlag): boolean {
    return super.isDisabled(feature);
  }
};

export default new FeatureFlags();
